import { useEffect, useRef, useState } from 'react'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { formsApi } from '@/api/formsApi'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import type { FormSubmission } from '@/types/cgt'

/** Mirrors `@MaxLength` on the server's UnlockSubmissionDto. */
const REASON_MAX_LENGTH = 2000

/**
 * Unlock a completed form so it can be corrected and completed again.
 *
 * The shared Part 11 signature plus a required reason, both recorded in the
 * form's audit trail. Cancelling sends nothing.
 */
export function UnlockFormDialog({
  submissionId,
  open,
  onOpenChange,
  signerName,
  onUnlocked,
  isKit = false,
}: {
  submissionId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  signerName?: string
  onUnlocked: (updated: FormSubmission) => void
  /** A kit form: unlocking it takes its kit back out of Kit Inventory (z8nrz7e2mu). */
  isKit?: boolean
}) {
  const [reason, setReason] = useState('')
  // Read at send time, not from the render that started signing: the password
  // check is async, and text typed meanwhile must not be dropped.
  const reasonRef = useRef('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Each unlock is its own deliberate act: never prefill the last reason.
  useEffect(() => {
    if (open) {
      setReason('')
      reasonRef.current = ''
      setError(null)
    }
  }, [open])

  const unlock = async (signingToken: string) => {
    setBusy(true)
    setError(null)
    try {
      const updated = await formsApi.unlockSubmission(
        submissionId,
        signingToken,
        reasonRef.current.trim(),
      )
      onUnlocked(updated)
      onOpenChange(false)
    } catch (err) {
      setError(apiErrorMessage(err, 'The form could not be unlocked.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SignatureDialog
      open={open}
      onOpenChange={onOpenChange}
      title='Unlock this form'
      description='Unlocking reopens the form for changes. Complete it again when you are done.'
      confirmLabel='Sign & Unlock'
      signerName={signerName}
      confirmDisabled={reason.trim() === ''}
      busy={busy}
      error={error}
      onConfirm={token => void unlock(token)}
    >
      <div className='space-y-1.5'>
        <Label htmlFor='unlock-reason'>
          Reason <span className='text-destructive'>*</span>
        </Label>
        <Textarea
          id='unlock-reason'
          rows={3}
          maxLength={REASON_MAX_LENGTH}
          value={reason}
          onChange={e => {
            setReason(e.target.value)
            reasonRef.current = e.target.value
          }}
          placeholder='Why does this completed form need changes?'
          autoFocus
        />
        <p className='text-xs text-muted-foreground'>
          Forms after this one that are already started stay open.
        </p>
        {isKit && (
          <p className='text-xs text-amber-700'>
            This is a kit form: unlocking it takes the kit out of Kit Inventory until the form is
            completed again. A kit that has already been used cannot be unlocked.
          </p>
        )}
      </div>
    </SignatureDialog>
  )
}
